import React, { useEffect, useCallback, useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";
import { useABFlag } from "@lululemon/mwa-ab-testing/esm";
import { useToast, LinkGenerator } from "@lululemon/ui-library";
import { log } from "@lululemon/mwa-utils";
import { getUnifiedIdFromPath } from "@lululemon/ecom-cdp/src/lib/cdp-utils";
import useComparisonFocusManagement from "./useComparisonFocusManagement";

import styles from "./useSelectiveComparison.module.scss";

// URLs to exclude from 3 mobile products AB test
const excludedPathsSet = new Set([
  "/c/women/_/N-7vf",
  "/c/whats-new/_/N-1z0xcuuZ8te",
  "/c/bestsellers/_/N-1z0xcuuZ8td",
  "/c/sale/_/N-1z0xcuuZ8t6",
  "/c/men/_/N-7tu",
  "/c/sale/_/N-1z0xcmkZ8t6",
  "/c/whats-new/_/N-1z0xcmkZ8te",
  "/c/bestsellers/_/N-1z0xcmkZ8td",
]);

const setProductsToCompareToSessionStorage = (value) => {
  try {
    window?.sessionStorage?.setItem("productsToCompare", JSON.stringify(value));
  } catch (error) {
    log.warning(error);
  }
};

const useSelectiveComparison = (asPath, categoryTitle, isMobile) => {
  // only enable Selective Comparison on CDPs, not on Search page
  const shouldDisplaySelectiveComparison = !!getUnifiedIdFromPath(asPath);
  const selectiveComparison3ProductsMobileABFlag = useABFlag(
    "selectiveComparison3ProductsMobileABFlag",
    false
  );

  const extractBasePath = (url) => {
    if (!url) return "";
    return url.split("?")[0].replace(/\/$/, "");
  };

  const cdpUrlBasePath = extractBasePath(asPath);

  const isExcludedPath = excludedPathsSet.has(cdpUrlBasePath);

  let maxProductsToCompare = 3;
  let allowScrollMobile = false;
  if (isMobile) {
    if (selectiveComparison3ProductsMobileABFlag) {
      maxProductsToCompare = isExcludedPath ? 2 : 3;
      allowScrollMobile = !isExcludedPath;
    } else {
      maxProductsToCompare = 2;
    }
  }

  const { locale } = useRouter();

  const { showToast } = useToast();
  const [productsToCompare, setProductsToCompare] = useState([]);

  const {
    focusComparisonDrawer,
    unFocusComparisonDrawer,
    comparisonDrawerRef,
  } = useComparisonFocusManagement();

  const clearAllProductsToCompare = () => {
    setProductsToCompare([]);
    setProductsToCompareToSessionStorage([]);
  };

  const removeProductToCompareById = useCallback(
    (productId) => {
      const newProductsToCompare = productsToCompare?.filter(
        (p) => p.productId !== productId
      );

      setProductsToCompare(newProductsToCompare);
      trackEvent(EVENT_CATEGORY.GENERAL_COMPONENT_INTERACTION, {
        component: {
          id: "cdp:compare:uncheck",
          type: "checkbox",
          text: "Compare",
          position: newProductsToCompare.length,
          totalCount: newProductsToCompare.length,
        },
      });

      setProductsToCompareToSessionStorage(newProductsToCompare);
    },
    [productsToCompare]
  );

  const showMaxProductsToastMessage = (hideToastBar = false) => {
    showToast({
      message: (
        <div className={`OneLinkTx ${styles.toastBar}`}>
          <span className="lll-text-body-1 lll-font-weight-medium">
            Only {`${maxProductsToCompare}`} products can be compared
          </span>
          <span className="lll-text-body-3">Clear one, and then try again</span>
        </div>
      ),
      action: (
        <LinkGenerator
          className="lll-link-primary"
          onClick={(event) => {
            event.preventDefault();
            showMaxProductsToastMessage(true);
          }}
          tabIndex={0}
        >
          Dismiss
        </LinkGenerator>
      ),
      className: classNames(styles.toast, {
        [styles.hideToast]: hideToastBar,
      }),
    });
  };

  const updateProductsToCompare = useCallback(
    (product) => {
      const { productId } = product;
      const shouldRemoveProduct = !!productsToCompare?.find(
        (p) => p.productId === productId
      );
      let newProductsToCompare;
      if (shouldRemoveProduct) {
        removeProductToCompareById(productId);
      } else {
        newProductsToCompare = [
          ...productsToCompare,
          { ...product, usrLocale: locale },
        ];
        const isMaxProductsExceeded =
          (!selectiveComparison3ProductsMobileABFlag &&
            isMobile &&
            newProductsToCompare.length > 2) ||
          newProductsToCompare.length > 3;

        if (isMaxProductsExceeded) {
          showMaxProductsToastMessage();
          return;
        }
        setProductsToCompare(newProductsToCompare);
        trackEvent(EVENT_CATEGORY.GENERAL_COMPONENT_INTERACTION, {
          component: {
            id: "cdp:compare:check",
            type: "checkbox",
            text: "Compare",
            position: newProductsToCompare.length,
            totalCount: newProductsToCompare.length,
          },
        });
        setProductsToCompareToSessionStorage(newProductsToCompare);
      }
    },
    [locale, productsToCompare, removeProductToCompareById]
  );

  const getComparePageURL = useCallback(() => {
    const cdpUrl = asPath;
    const cdpLabel = categoryTitle;
    const products = productsToCompare
      .map((p) => p?.productUrl || p?.pdpUrl)
      .join("|");

    const qs = {
      products,
      cdpUrl,
      cdpLabel,
    };

    const params = new URLSearchParams(qs);

    return `/c/compare?${params.toString()}`;
  }, [asPath, categoryTitle, productsToCompare]);

  useEffect(() => {
    const setInitialProductsToCompareFromSessionStorage = () => {
      try {
        let compareProducts =
          JSON.parse(window.sessionStorage.getItem("productsToCompare")) || [];

        if (compareProducts?.[0]?.usrLocale !== locale) {
          compareProducts = [];
          setProductsToCompareToSessionStorage(compareProducts);
        }

        setProductsToCompare(compareProducts);
      } catch (error) {
        log.warning(error);
      }
    };

    setInitialProductsToCompareFromSessionStorage();
  }, []);

  return {
    clearAllProductsToCompare,
    comparisonDrawerRef,
    focusComparisonDrawer,
    getComparePageURL,
    productsToCompare,
    removeProductToCompareById,
    selectiveComparison3ProductsMobileABFlag,
    shouldDisplaySelectiveComparison,
    unFocusComparisonDrawer,
    updateProductsToCompare,
    maxProductsToCompare,
    allowScrollMobile,
  };
};

export default useSelectiveComparison;
